import { useQuery } from '@tanstack/react-query';

interface UnmatchedRevenueCatResponse {
  users: Array<{ app_user_id: string; email?: string }>;
  pagination?: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
  message?: string;
}

async function fetchUnmatchedRevenueCat(): Promise<UnmatchedRevenueCatResponse> {
  const response = await fetch('/api/v1/admin/users/unmatched/revenuecat');
  const payload = await response.json().catch(() => null) as {
    errorMessage?: string;
    users?: Array<{ app_user_id: string; email?: string }>;
    pagination?: UnmatchedRevenueCatResponse['pagination'];
    message?: string;
  } | null;
  if (!response.ok) {
    throw new Error(payload?.errorMessage || 'Failed to fetch unmatched RevenueCat users');
  }
  return {
    users: payload?.users || [],
    pagination: payload?.pagination,
    message: payload?.message,
  };
}

export default function UnmatchedPage() {
  const { data: revenuecatData, isLoading: revenuecatLoading, error: revenuecatError } = useQuery({
    queryKey: ['admin-unmatched-revenuecat'],
    queryFn: fetchUnmatchedRevenueCat,
  });

  return (
    <div>
      <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
        Unmatched Users
      </h2>
      <p className="mt-2 text-sm text-gray-500">Subscribers in RevenueCat but not in D1</p>
      <div className="mt-6 grid grid-cols-1 gap-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">RevenueCat Unmatched</h3>
            {revenuecatLoading ? (
              <div className="mt-4 text-center text-gray-500">Loading...</div>
            ) : revenuecatError ? (
              <div className="mt-4 text-center text-red-500">Error: {revenuecatError.message}</div>
            ) : revenuecatData?.users && revenuecatData.users.length > 0 ? (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-4">{revenuecatData.users.length} users found</p>
                <ul className="divide-y divide-gray-200">
                  {revenuecatData.users.map((user: { app_user_id: string; email?: string }) => (
                    <li key={user.app_user_id} className="py-3 flex justify-between">
                      <span className="text-sm text-gray-900">{user.email || user.app_user_id}</span>
                      <span className="text-xs text-gray-500">{user.app_user_id}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="mt-4 text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="mt-2 text-sm">No unmatched RevenueCat users found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
